import React from 'react';
import Icon from '../../../components/AppIcon';

const ActivityFeed = ({ activities = [] }) => {
  const getActivityIcon = (type) => {
    switch (type) {
      case 'user_registration':
        return 'UserPlus';
      case 'support_ticket':
        return 'MessageCircle';
      case 'subscription':
        return 'CreditCard';
      case 'video_render':
        return 'Play';
      case 'payment':
        return 'DollarSign';
      case 'project_created':
        return 'FolderPlus';
      case 'project_updated':
        return 'Edit';
      case 'video_uploaded':
        return 'Upload';
      case 'credit_added':
        return 'Plus';
      default:
        return 'Activity';
    }
  };

  const getActivityColor = (type, priority) => {
    if (priority === 'high') return 'text-red-600';
    
    switch (type) {
      case 'user_registration':
        return 'text-green-600';
      case 'support_ticket':
        return 'text-orange-600';
      case 'subscription':
        return 'text-blue-600';
      case 'video_render':
        return 'text-purple-600';
      case 'payment':
        return 'text-emerald-600';
      case 'project_created':
        return 'text-indigo-600';
      case 'project_updated':
        return 'text-amber-600';
      case 'video_uploaded':
        return 'text-cyan-600';
      case 'credit_added':
        return 'text-teal-600';
      default:
        return 'text-gray-600';
    }
  };

  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return 'Recently';
    
    const now = new Date();
    const activityTime = new Date(timestamp);
    const diffMs = now - activityTime;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    return activityTime?.toLocaleDateString();
  };

  const formatActivityTitle = (activity) => {
    if (activity?.action) {
      // Format database actions into readable titles
      return activity?.action
        ?.split('_')
        ?.map(word => word?.charAt(0)?.toUpperCase() + word?.slice(1))
        ?.join(' ');
    }
    return activity?.title || 'System Activity';
  };

  const formatActivityDescription = (activity) => {
    if (activity?.details && typeof activity?.details === 'object') {
      // Extract meaningful info from details object
      const details = activity?.details;
      if (details?.project_name) {
        return `Project: ${details?.project_name}`;
      }
      if (details?.user_email) {
        return `User: ${details?.user_email}`;
      }
      if (details?.description) {
        return details?.description;
      }
    }
    
    return activity?.description || 'System activity performed';
  };

  if (!activities?.length) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-foreground">Recent Activity</h3>
          <Icon name="Activity" size={20} className="text-muted-foreground" />
        </div>
        
        <div className="text-center py-8">
          <Icon name="Clock" size={48} className="text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">No recent activities to display</p>
          <p className="text-sm text-muted-foreground mt-2">
            Activities will appear here as users interact with the platform
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-foreground">Recent Activity</h3>
        <div className="flex items-center space-x-2">
          <span className="text-sm text-muted-foreground">{activities?.length} activities</span>
          <Icon name="Activity" size={20} className="text-muted-foreground" />
        </div>
      </div>
      
      <div className="space-y-4 max-h-96 overflow-y-auto">
        {activities?.map((activity, index) => (
          <div key={activity?.id || index} className="flex items-start space-x-3 p-3 rounded-lg hover:bg-muted/50 transition-colors">
            <div className={`p-2 rounded-full bg-muted ${getActivityColor(activity?.action || activity?.type, activity?.priority)}`}>
              <Icon 
                name={getActivityIcon(activity?.action || activity?.type)} 
                size={16} 
                className="text-white" 
              />
            </div>
            
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="text-sm font-medium text-foreground">
                    {formatActivityTitle(activity)}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {formatActivityDescription(activity)}
                  </p>
                  {activity?.user?.full_name && (
                    <p className="text-xs text-muted-foreground mt-1">
                      by {activity?.user?.full_name}
                    </p>
                  )}
                </div>
                
                <div className="text-xs text-muted-foreground whitespace-nowrap">
                  {formatTimeAgo(activity?.timestamp || activity?.created_at)}
                </div>
              </div>
              
              {activity?.priority === 'high' && (
                <div className="mt-2">
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                    High Priority
                  </span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      
      <div className="mt-4 pt-4 border-t border-border">
        <button className="w-full text-sm text-primary hover:text-primary/80 transition-colors">
          View All Activities →
        </button>
      </div>
    </div>
  );
};

export default ActivityFeed;